import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { siteService } from 'components/api/sites';
import { Site, SitesParams, SitesResponse } from 'types/site';

interface SitesState {
  data: Site[];
  total: number;
  isLoading: boolean;
  error: string | null;
  params: SitesParams;
}

const initialState: SitesState = {
  data: [],
  total: 0,
  isLoading: false,
  error: null,
  params: {
    page: 1,
    limit: 10,
    sortField: 'id',
    sortValue: 'DESC',
  },
};

export const fetchSites = createAsyncThunk(
  'sites/fetchSites',
  async (params: SitesParams, { rejectWithValue }) => {
    try {
      const response = await siteService.getSites(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch sites');
    }
  },
);

const sitesSlice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    setParams: (state, action: PayloadAction<Partial<SitesParams>>) => {
      state.params = { ...state.params, ...action.payload };
    },
    updateSiteSettingLocal: (
      state,
      action: PayloadAction<{ id: number; settingKey: string; value: any }>,
    ) => {
      const site = state.data.find((s) => s.id === action.payload.id);
      if (site && site.settings) {
        site.settings = { ...site.settings, [action.payload.settingKey]: action.payload.value };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSites.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSites.fulfilled, (state, action: PayloadAction<SitesResponse>) => {
        state.isLoading = false;
        state.data = action.payload.data?.docs || [];
        state.total = action.payload.data?.totalDocs || 0;
      })
      .addCase(fetchSites.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setParams, updateSiteSettingLocal } = sitesSlice.actions;
export default sitesSlice.reducer;
